"use client";

import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

function useIsMobile() {
  const [v, setV] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    setV(mq.matches);
    const h = (e: MediaQueryListEvent) => setV(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return v;
}

import { MapPin, Images, Play, Shuffle, Square, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Route } from "lucide-react";
import { UserResponse } from "@/api-client";
import { SLIDESHOW_INTERVALS, SlideshowMs } from "../hooks/useSlideshow";
import { MapStyleKey } from "@/features/map/mapTheme";
import { useTheme } from "@/features/theme";

interface TopGlassMenuProps {
  visible: boolean;
  isMapInteracting: boolean;
  filterPanelOpen?: boolean;
  anyPanelOpen?: boolean;
  isGlowing: boolean;
  mapStyle: MapStyleKey;
  onExpandedChange?: (expanded: boolean) => void;
  user: UserResponse | null;
  showMyProfileOnly: boolean;
  onToggleContributions: () => void;
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
  isRouteBuilderOpen: boolean;
  onToggleRouteBuilder: () => void;
  isGpxRouteOpen: boolean;
  onToggleGpxRoute: () => void;
  slideshowInterval: SlideshowMs | null;
  slideshowMenuOpen: boolean;
  slideshowMenuRef: React.RefObject<HTMLDivElement | null>;
  onSlideshowButtonClick: () => void;
  onSlideshowSelect: (ms: SlideshowMs | null) => void;
  slideshowDeckRef: React.RefObject<string[]>;
}

const BASE_SHADOW = "0 25px 50px -12px rgb(0 0 0 / 0.25)";
const GLOW_LOW     = `${BASE_SHADOW}, 0 0 0 1.5px rgba(255,255,255,0.12), 0 0 16px 4px rgba(255,255,255,0.06)`;
const GLOW_HIGH    = `${BASE_SHADOW}, 0 0 0 2px rgba(255,255,255,0.26), 0 0 26px 7px rgba(255,255,255,0.12)`;
const GLOW_SETTLED = `${BASE_SHADOW}, 0 0 0 1.5px rgba(255,255,255,0.60)`;
const GLOW_LOW_LIGHT     = `${BASE_SHADOW}, 0 0 0 1.5px rgba(0,0,0,0.06)`;
const GLOW_HIGH_LIGHT    = `${BASE_SHADOW}, 0 0 0 2px rgba(0,0,0,0.11)`;
const GLOW_SETTLED_LIGHT = `${BASE_SHADOW}, 0 0 0 1.5px rgba(0,0,0,0.20)`;

// Desktop: width-based side expansion
const sideButtonVariants = {
  hidden:  { opacity: 0, width: 0, marginLeft: 0, marginRight: 0, paddingLeft: 0, paddingRight: 0 },
  visible: { opacity: 1, width: "auto", marginLeft: 0, marginRight: 0, paddingLeft: undefined, paddingRight: undefined,
    transition: { type: "spring" as const, stiffness: 340, damping: 28 } },
  exit:    { opacity: 0, width: 0, paddingLeft: 0, paddingRight: 0,
    transition: { duration: 0.18, ease: "easeIn" as const } },
};

const dividerVariants = {
  hidden:  { opacity: 0, width: 0, marginLeft: 0, marginRight: 0 },
  visible: { opacity: 1, width: 1, marginLeft: 4, marginRight: 4, transition: { duration: 0.2 } },
  exit:    { opacity: 0, width: 0, marginLeft: 0, marginRight: 0, transition: { duration: 0.15 } },
};

export default function TopGlassMenu({
  visible,
  isMapInteracting,
  filterPanelOpen = false,
  anyPanelOpen = false,
  isGlowing,
  mapStyle,
  onExpandedChange,
  user,
  showMyProfileOnly, onToggleContributions,
  isSidebarOpen,    onToggleSidebar,
  isRouteBuilderOpen, onToggleRouteBuilder,
  isGpxRouteOpen, onToggleGpxRoute,
  slideshowInterval, slideshowMenuOpen, slideshowMenuRef,
  onSlideshowButtonClick, onSlideshowSelect, slideshowDeckRef,
}: TopGlassMenuProps) {
  const { isDark } = useTheme();
  const isMobile = useIsMobile();
  const ALWAYS_EXPANDED = !isMobile;

  const [expanded, setExpanded] = useState(true);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { setExpanded(ALWAYS_EXPANDED); }, [ALWAYS_EXPANDED]);
  useEffect(() => { onExpandedChange?.(expanded); }, [expanded, onExpandedChange]);

  const handleMouseEnter = () => {
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    setExpanded(true);
  };
  const handleMouseLeave = () => {
    if (ALWAYS_EXPANDED) return;
    leaveTimerRef.current = setTimeout(() => {
      if (!slideshowMenuOpen) setExpanded(false);
    }, 120);
  };

  const handleMainButtonClick = () => {
    if (isMobile) {
      setExpanded((v) => !v);
    } else {
      onToggleSidebar();
    }
  };

  // Shared button style helpers
  const actionBtn = (active: boolean, activeColor = "bg-blue-600") =>
    `flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all text-[10px] font-black uppercase tracking-[0.15em] whitespace-nowrap ${
      active
        ? `${activeColor} text-white shadow-lg`
        :"text-tone-200 hover:text-ink hover:bg-ink/10"
    }`;

  // ── Mobile dropdown row of buttons ────────────────────────────────────────
  const mobileButtons = (
    <AnimatePresence initial={false}>
      {expanded && (
        <motion.div
          key="mobile-buttons"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto", transition: { type: "spring", stiffness: 340, damping: 30 } }}
          exit={{ opacity: 0, height: 0, transition: { duration: 0.18, ease: "easeIn" } }}
          className="overflow-hidden w-full"
        >
          {/* Divider */}
          <div className={`h-px mx-2 mb-1 bg-ink/10`} />
          {/* Button grid */}
          <div className="flex flex-wrap gap-1 px-1 pb-1">
            {user && (
              <button
                onClick={() => { onToggleContributions(); setExpanded(false); }}
                className={actionBtn(showMyProfileOnly)}
              >
                <MapPin className={`w-3.5 h-3.5 shrink-0 ${showMyProfileOnly ? "animate-bounce" : ""}`} />
                Mine
              </button>
            )}
            {user && (
              <button
                onClick={() => { onToggleRouteBuilder(); setExpanded(false); }}
                className={actionBtn(isRouteBuilderOpen, "bg-indigo-600")}
              >
                <Route className="w-3.5 h-3.5 shrink-0" />
                Route
              </button>
            )}
            {user && (
              <button
                onClick={() => { onToggleGpxRoute(); setExpanded(false); }}
                className={actionBtn(isGpxRouteOpen, "bg-indigo-600")}
              >
                <Route className="w-3.5 h-3.5 shrink-0" />
                GPX
              </button>
            )}
            <button
              onClick={() => { onToggleSidebar(); setExpanded(false); }}
              className={actionBtn(isSidebarOpen)}
            >
              <Images className="w-3.5 h-3.5 shrink-0" />
              Explore
            </button>
            <button
              onClick={onSlideshowButtonClick}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all text-[10px] font-black uppercase tracking-[0.15em] whitespace-nowrap ${
                slideshowInterval
                  ? "bg-emerald-600 text-white shadow-lg shadow-emerald-500/20"
                  :"text-tone-400 hover:text-ink hover:bg-ink/5"
              }`}
            >
              {slideshowInterval
                ? <><Shuffle className="w-3.5 h-3.5 animate-pulse shrink-0" />{SLIDESHOW_INTERVALS.find((i) => i.ms === slideshowInterval)?.shortLabel}</>
                : <><Play className="w-3.5 h-3.5 shrink-0" />Slideshow</>
              }
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-tour-id="glass-menu"
          initial={{ top: "8px", x: "-50%", opacity: 0, scale: 1 }}
          animate={(() => {
            const glowLow     = isDark ? GLOW_LOW     : GLOW_LOW_LIGHT;
            const glowHigh    = isDark ? GLOW_HIGH    : GLOW_HIGH_LIGHT;
            const glowSettled = isDark ? GLOW_SETTLED : GLOW_SETTLED_LIGHT;
            const ring = isGlowing ? [glowLow, glowHigh, glowLow] : glowSettled;
            if (isMobile && isSidebarOpen)
              return { top: "30vh", x: "-50%", opacity: 0, scale: 0.85, boxShadow: BASE_SHADOW };
            if (isMapInteracting || filterPanelOpen || anyPanelOpen)
              return { top: "8px", x: "-50%", opacity: 1, scale: 1, boxShadow: ring };
            return { top: isMobile ? "18px" : "164px", x: "-50%", opacity: 1, scale: 1, boxShadow: ring };
          })()}
          exit={{ x: "-50%", opacity: 0, scale: 0.9 }}
          transition={{
            top:       { type: "spring", stiffness: 260, damping: 28 },
            opacity:   { duration: isMobile && isSidebarOpen ? 0.2 : 0.35 },
            scale:     { type: "spring", stiffness: 300, damping: 25 },
            boxShadow: isGlowing
              ? { repeat: Infinity, duration: 2, ease: "easeInOut" }
              : { duration: 0.3, ease: "easeOut" },
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={`absolute left-1/2 z-[40] backdrop-blur-2xl rounded-2xl p-1 shadow-2xl ${
            isMobile && isSidebarOpen ? "pointer-events-none" : ""
          } ${
            isMobile
              ? "flex flex-col items-stretch"   // mobile: vertical column
              : "flex flex-row items-center"     // desktop: horizontal row
          } ${isDark ? "bg-black/50 border border-white/10" : "bg-white/55 border border-white/70 shadow-[0_8px_32px_rgba(0,0,0,0.07)]"}`}
        >
          {/* ── Mobile layout: main button on top, buttons drop below ── */}
          {isMobile ? (
            <>
              <button
                onClick={handleMainButtonClick}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-all text-[10px] font-black uppercase tracking-[0.15em] ${
                  isDark
                    ? "text-white hover:bg-white/10"
                    : "text-neutral-500 hover:text-neutral-700 hover:bg-black/[0.04]"
                }`}
              >
                <Images className="w-3.5 h-3.5 shrink-0" />
                360Photos
                {expanded
                  ? <ChevronUp className="w-3 h-3 ml-1 opacity-40" />
                  : <ChevronDown className="w-3 h-3 ml-1 opacity-40" />
                }
              </button>
              {mobileButtons}
            </>
          ) : (
            <>
              {/* ── Desktop: left buttons ── */}
              <AnimatePresence initial={false}>
                {expanded && user && (
                  <motion.div
                    key="left-buttons"
                    variants={sideButtonVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="overflow-hidden flex items-center"
                  >
                    <button onClick={onToggleContributions} className={actionBtn(showMyProfileOnly)}>
                      <MapPin className={`w-3.5 h-3.5 shrink-0 ${showMyProfileOnly ? "animate-bounce" : ""}`} />
                      Contributions
                    </button>
                    <button onClick={onToggleRouteBuilder} className={actionBtn(isRouteBuilderOpen, "bg-indigo-600")}>
                      <Route className="w-3.5 h-3.5 shrink-0" />
                      Route
                    </button>
                    <button onClick={onToggleGpxRoute} className={actionBtn(isGpxRouteOpen, "bg-indigo-600")}>
                      <Route className="w-3.5 h-3.5 shrink-0" />
                      GPX
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Left divider */}
              <AnimatePresence initial={false}>
                {expanded && user && (
                  <motion.div key="divider-left" variants={dividerVariants} initial="hidden" animate="visible" exit="exit"
                    className={`h-4 shrink-0 bg-ink/10`} />
                )}
              </AnimatePresence>

              {/* Main button */}
              <button
                onClick={handleMainButtonClick}
                className={actionBtn(isSidebarOpen)}
              >
                <Images className="w-3.5 h-3.5 shrink-0" />
                360Photos
                <AnimatePresence initial={false} mode="wait">
                  {!expanded && (
                    <motion.span key="hint" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }} className="flex items-center gap-0.5 ml-1">
                      <ChevronLeft className="w-2.5 h-2.5 text-ink/30" />
                      <ChevronRight className="w-2.5 h-2.5 text-ink/30" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>

              {/* Right divider */}
              <AnimatePresence initial={false}>
                {expanded && (
                  <motion.div key="divider-right" variants={dividerVariants} initial="hidden" animate="visible" exit="exit"
                    className={`h-4 shrink-0 bg-ink/10`} />
                )}
              </AnimatePresence>

              {/* Slideshow */}
              <AnimatePresence initial={false}>
                {expanded && (
                  <motion.div key="slideshow" variants={sideButtonVariants} initial="hidden" animate="visible" exit="exit"
                    ref={slideshowMenuRef} className="overflow-visible">
                    <button
                      onClick={onSlideshowButtonClick}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all text-[10px] font-black uppercase tracking-[0.15em] whitespace-nowrap ${
                        slideshowInterval
                          ? "bg-emerald-600 text-white shadow-lg shadow-emerald-500/20"
                          : slideshowMenuOpen
                            ?"bg-ink/10 text-ink"
                            :"text-tone-400 hover:text-ink hover:bg-ink/5"
                      }`}
                    >
                      {slideshowInterval ? (
                        <><Shuffle className="w-3.5 h-3.5 animate-pulse shrink-0" />{SLIDESHOW_INTERVALS.find((i) => i.ms === slideshowInterval)?.shortLabel}</>
                      ) : (
                        <><Play className="w-3.5 h-3.5 shrink-0" />Slideshow</>
                      )}
                    </button>

                    <AnimatePresence>
                      {slideshowMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -6, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -6, scale: 0.97 }}
                          transition={{ type: "spring", stiffness: 400, damping: 28 }}
                          className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-52 rounded-2xl p-2 z-50 ${isDark ? "bg-neutral-900 border border-white/10 shadow-2xl" : "bg-white border border-black/[0.08] shadow-[0_8px_32px_rgba(0,0,0,0.12)]"}`}
                        >
                          <p className={`text-[9px] font-black uppercase tracking-[0.2em] px-3 pt-1 pb-2 text-tone-500`}>
                            Change photo every…
                          </p>
                          {SLIDESHOW_INTERVALS.map((opt) => (
                            <button key={opt.ms}
                              onClick={() => { onSlideshowSelect(slideshowInterval === opt.ms ? null : opt.ms); slideshowDeckRef.current = []; }}
                              className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                                slideshowInterval === opt.ms
                                  ? "bg-emerald-600 text-white"
                                  :"text-tone-300 hover:bg-ink/10 hover:text-ink"
                              }`}
                            >
                              {opt.label}
                              {slideshowInterval === opt.ms && <Square className="w-3 h-3 opacity-70" />}
                            </button>
                          ))}
                          {slideshowInterval && (
                            <>
                              <div className={`h-[1px] my-1.5 mx-2 bg-ink/10`} />
                              <button onClick={() => onSlideshowSelect(null)}
                                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-red-400 hover:bg-red-500/10 transition-all">
                                <Square className="w-3 h-3" />
                                Stop slideshow
                              </button>
                            </>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
